import React, {
    useEffect, useState, forwardRef,
    useRef, useMemo, useCallback,
    ReactNode,
    LegacyRef,
} from 'react';
import RcTooltip, { TooltipRef } from 'rc-tooltip';
import { validatePlacement } from './utils/placements';

import withTheme from '../../hocs/withTheme';
import getDistanceByPlacement from './utils/placementOffset';
import { ActionType } from 'rc-trigger/lib/interface';
import './Popover.css';

const DEFAULT_PLACEMENT = 'top';

type PopoverProps = {
    title: ReactNode,
    content: ReactNode,
    defaultVisible: boolean,
    visible: boolean,
    onVisibleChange: ((visible: boolean) => void) | undefined,
    placement: string,
    trigger?: ActionType | ActionType[],
    arrowPointAtCenter: boolean,
    autoAdjustOverflow: boolean,
    overlayClassName: string,
    children: ReactNode,
    innerRef?: LegacyRef<TooltipRef> | undefined; 
};

const Popover = (props: PopoverProps) => {
    const {
        children, visible = false, defaultVisible = false, onVisibleChange = () => {},
        overlayClassName = '', title = '', content = null, placement = 'top', trigger = ['click'],
        innerRef: ref = null,
    } = props;
    const childElement = useRef<HTMLElement | null>(null);
    const [localVisible, setLocalVisible] = useState(!!visible || !!defaultVisible);

    useEffect(() => {
        if ('visible' in props) {
            setLocalVisible(visible);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);
    const isNoTitle = () => !title && !content && title !== 0;

    const handleVisibleChange = (vis) => {
        if (!('visible' in props)) {
            setLocalVisible(isNoTitle() ? false : vis);
        }
        if (onVisibleChange && !isNoTitle()) {
            onVisibleChange(vis);
        }
    };

    const observerCallback = useCallback((domNode) => {
        if (domNode) {
            const arrowDistance = parseFloat(global.getComputedStyle(domNode).getPropertyValue('--popover-arrow-distance'));
            const arrowEl = domNode.querySelector('.rc-tooltip-arrow');
            const innerEl = domNode.querySelector('.rc-tooltip-inner');
            const childElementRect = childElement?.current?.getBoundingClientRect();
            const arrowElRect = arrowEl.getBoundingClientRect();

            const distance = getDistanceByPlacement({
                placement, arrowEl, innerEl, arrowDistance,
            });

            const offset = childElementRect ? Math.ceil(childElementRect?.left + (childElementRect.width / 2)
                - arrowElRect.left - arrowElRect.width / 2) : 0;

            if (placement.indexOf('top') >= 0 || placement.indexOf('bottom') >= 0) {
                if (Math.abs(offset) <= (childElementRect?.width || 0) * 2
                    && Math.abs(offset) < parseFloat(domNode.style.left)) {
                    // eslint-disable-next-line no-param-reassign
                    domNode.style.left = `${parseFloat(domNode.style.left) + offset}px`;
                }
            }

            if (distance?.style && distance?.offset) {
                innerEl.style[distance.style] = `${distance.offset}px`;
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [childElement?.current]);

    const observerPopover = useMemo(() => new global.MutationObserver((event) => {
        try {
            const domNode = Array.isArray(event) && event[0] && event[0].target;

            observerCallback(domNode);
        } catch (e) {
            console.warn(e);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), []);

    const onPopupAlign = (domNode) => {
        observerCallback(domNode);
        observerPopover.observe(domNode, { attributes: true, attributeFilter: ['style'] });
    };

    const renderOverlay = () => {
        if (title === 0) {
            return title;
        }

        return content || title || '';
    };

    const getTooltipContainer = (domNode) => {
        childElement.current = domNode;

        return document.body;
    };
    const getOverlay = () => (
        <>
            {title && <div className="rc-tooltip-title">{renderOverlay()}</div>}
            {content && <div className="rc-tooltip-inner-content">{renderOverlay()}</div>}
        </>
    );
    const getVisible = (vis) => {
        if (!('visible' in props) && isNoTitle()) {
            return false;
        }

        return vis;
    };

    useEffect(() => {
        if (localVisible === false) {
            observerPopover.disconnect();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localVisible]);

    const childrenRender: any  = typeof children === 'string'
        ? <span>{children}</span> : children;

    return (
        <RcTooltip
            {...props}
            classNames={{root: `Popover ${overlayClassName}`}}
            placement={validatePlacement(placement, DEFAULT_PLACEMENT)}
            trigger={trigger as ActionType | ActionType[] | undefined}
            ref={ref}
            visible={getVisible(localVisible)}
            arrowContent={<span className="rc-tooltip-arrow-content" />}
            onPopupAlign={onPopupAlign}
            onVisibleChange={handleVisibleChange}
            getTooltipContainer={getTooltipContainer}
            overlay={getOverlay()}
            destroyPopupOnHide
        >
            {childrenRender}
        </RcTooltip>
    );
};

export default forwardRef<TooltipRef, PopoverProps>((props, ref) => withTheme(Popover)({ ...props, innerRef: ref }));
